import * as React from "react";
import {
  composeRenderProps,
  Tab as RACTab,
  TabList as RACTabList,
  TabListProps as RACTabListProps,
  TabPanel as RACTabPanel,
  TabProps as RACTabProps,
  Tabs as RACTabs,
  TabsProps as RACTabsProps,
  TabPanelProps,
} from "react-aria-components";
import { focusRing } from "../../utils";

export type TabVariant = "primary" | "default" | "brand";
type NormalizedTabVariant = "primary";

function normalizeTabVariant(_variant: TabVariant | undefined): NormalizedTabVariant {
  return "primary";
}

// Create a context to pass the variant down to child components
const TabsContext = React.createContext<{ variant: NormalizedTabVariant }>({
  variant: "primary",
});

type TabProps = RACTabProps & {
  isSelected?: boolean;
  id?: string;
};

export type TabsProps = RACTabsProps & {
  variant?: TabVariant;
};

export type TabListProps<T extends object> = RACTabListProps<T> & {
  variant?: TabVariant;
  /**
   * Makes the tab list sticky at the top of its scroll container
   * @default false
   */
  sticky?: boolean;
};

function getTabsStyles({
  orientation,
  className = "",
}: {
  orientation: "horizontal" | "vertical";
  className?: string;
}) {
  const baseStyles = "flex flex-col w-full";
  const orientationStyles = {
    horizontal: "",
    vertical: "flex-row",
  };

  return `${baseStyles} ${orientationStyles[orientation] || ""} ${className}`.trim();
}

/**
 * Tabs
 *
 * Tabbed interface with styled tabs and panels.
 * @param variant - "primary" for emphasized active styling, "secondary" for lower-emphasis active styling
 */
export function Tabs({ variant = "primary", ...props }: TabsProps) {
  const normalizedVariant = normalizeTabVariant(variant);
  return (
    <TabsContext.Provider value={{ variant: normalizedVariant }}>
      <div className="w-full h-auto">
        <RACTabs
          {...props}
          className={composeRenderProps(props.className, (className, renderProps) =>
            getTabsStyles({ ...renderProps, className })
          )}
        />
      </div>
    </TabsContext.Provider>
  );
}

function getTabListStyles({
  orientation,
  sticky = false,
  className = "",
}: {
  orientation: "horizontal" | "vertical";
  sticky?: boolean;
  className?: string;
}) {
  const baseStyles =
    "flex gap-10 relative after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-border-muted";
  const orientationStyles = {
    horizontal: "flex-row w-full flex-nowrap overflow-x-auto scrollbar-hide",
    vertical: "flex-col items-start",
  };
  const stickyStyles = sticky ? "sticky top-0 bg-background-surface z-10" : "";

  return `${baseStyles} ${orientationStyles[orientation] || ""} ${stickyStyles} ${className}`.trim();
}

/**
 * TabList container.
 */
export function TabList<T extends object>({ variant, sticky = false, ...props }: TabListProps<T>) {
  // If variant is passed directly to TabList, use it; otherwise inherit from context
  const context = React.useContext(TabsContext);
  const effectiveVariant = normalizeTabVariant(variant ?? context.variant);

  return (
    <TabsContext.Provider value={{ variant: effectiveVariant }}>
      <RACTabList
        {...props}
        className={composeRenderProps(props.className, (className, renderProps) =>
          getTabListStyles({
            ...renderProps,
            sticky,
            className: className || "",
          })
        )}
      />
    </TabsContext.Provider>
  );
}

function getTabStyles({
  isSelected,
  isDisabled,
  variant,
  className = "",
}: {
  isSelected: boolean;
  isDisabled: boolean;
  variant: NormalizedTabVariant;
  className?: string;
}) {
  const baseStyles =
    "flex items-center cursor-default px-1 py-2 font-medium transition-all hover:cursor-pointer relative shrink-0 whitespace-nowrap";
  const focusRingStyles =
    `${focusRing()} ${variant === "primary" ? "focus-visible:outline-action-primary" : ""}`.trim();

  let stateStyles = "";
  if (isDisabled) {
    stateStyles = "text-text-disabled forced-colors:text-[GrayText] cursor-not-allowed";
  } else if (isSelected) {
    stateStyles = "text-action-primary border-b-2 border-action-primary z-10 forced-colors:bg-[Highlight]";
  } else {
    // Inactive tabs - more muted like Radix UI
    stateStyles = "text-text-disabled hover:text-text-secondary";
  }

  return `${baseStyles} ${focusRingStyles} ${stateStyles} ${className}`.trim();
}

/**
 * Tab trigger element.
 */
export function Tab(props: TabProps) {
  const { variant } = React.useContext(TabsContext);

  return (
    <RACTab
      {...props}
      data-testid={props.id ? `tab-${props.id}` : undefined}
      className={composeRenderProps(props.className, (className, renderProps) =>
        getTabStyles({
          ...renderProps,
          className,
          isSelected: renderProps.isSelected,
          variant,
        })
      )}
    />
  );
}

function getTabPanelStyles({ className = "" }: { className?: string }) {
  const baseStyles = "block w-full mt-4 text-text-primary";
  const focusRingStyles = focusRing();

  return `${baseStyles} ${focusRingStyles} ${className}`.trim();
}

/**
 * TabPanel content area.
 */
export function TabPanel(props: TabPanelProps) {
  return (
    <RACTabPanel
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        getTabPanelStyles({
          ...renderProps,
          className: className || "",
        })
      )}
    />
  );
}
